import React, { useEffect, useState } from "react";
import { Wallet as WalletIcon, AlertTriangle, Plus, ArrowDownCircle, ArrowUpCircle, Sparkles } from "lucide-react";
import api, { friendlyMessage } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { formatINR, formatINRPrecise } from "../../lib/utils.fmt";
import { toast } from "sonner";
import EmptyState, { LoadingState } from "../../components/EmptyState";
import { openRazorpayCheckout, fetchRazorpayOrder } from "../../lib/razorpay";

const QUICK = [100, 250, 500, 1000, 2500];

export default function AdminWallet() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(500);

  const load = async () => {
    setError(null);
    try {
      const { data } = await api.get("/api/admin/wallet");
      setData(data);
    } catch (e) {
      setError(friendlyMessage(e));
    }
  };
  useEffect(() => { load(); }, []);

  const recharge = async () => {
    if (amount <= 0) { toast.error("Pick a valid amount"); return; }
    setBusy(true);
    try {
      // 1. Try to create a Razorpay order (gracefully falls back to mock)
      const order = await fetchRazorpayOrder(`/api/admin/wallet/razorpay-order?amount=${amount}`);
      if (order.mode === "mock") {
        // Instant credit fallback when super-admin hasn't configured Razorpay yet
        const { data } = await api.post("/api/admin/wallet/recharge", { amount, method: "mock" });
        toast.success(`Recharged ${formatINR(amount)} (mock — ask super admin to configure Razorpay)`);
        setOpen(false); load();
        return;
      }
      // 2. Real Razorpay flow
      try {
        const res = await openRazorpayCheckout({
          key: order.key_id,
          amount: order.amount_paise,
          currency: order.currency,
          order_id: order.order_id,
          name: "DairyFresh Platform Wallet",
          description: `Top-up ${formatINR(amount)} for OTP credits`,
        });
        await api.post("/api/admin/wallet/recharge", {
          amount, method: "razorpay",
          razorpay_payment_id: res.razorpay_payment_id,
          razorpay_order_id: res.razorpay_order_id,
          razorpay_signature: res.razorpay_signature,
        });
        toast.success(`Recharged ${formatINR(amount)}!`);
        setOpen(false); load();
      } catch (e) {
        if (e?.cancelled) { /* user closed checkout — no toast */ }
        else toast.error(e?.description || e?.response?.data?.detail || "Payment verification failed");
      }
    } catch (e) {
      toast.error(e?.description || e?.response?.data?.detail || "Recharge failed");
    } finally { setBusy(false); }
  };

  if (error) {
    return (
      <EmptyState icon={AlertTriangle} title="Couldn't load the wallet" description={error}
        action={<button onClick={load} className="h-10 px-4 rounded-2xl bg-brand-sky text-white text-sm font-bold">Retry</button>}
        fullHeight />
    );
  }
  if (!data) return <LoadingState fullHeight />;

  const { wallet, transactions, otp_charge, low_balance, blocked } = data;

  return (
    <div className="space-y-6 page-enter" data-testid="admin-wallet-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Platform Billing</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Wallet & OTP credits</h1>
          <p className="text-sm text-neutral-600 mt-1">
            Customers' OTPs are sent from this balance · <b>{formatINRPrecise(otp_charge)} per OTP</b>
          </p>
        </div>
        <Button onClick={() => setOpen(true)} className="h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold px-6" data-testid="open-recharge-btn">
          <Plus className="w-4 h-4 mr-2" /> Recharge
        </Button>
      </div>

      {/* Balance hero */}
      <div className={`rounded-3xl p-6 text-white ${blocked ? "bg-gradient-to-br from-brand-clay to-red-700"
                                                    : low_balance ? "bg-gradient-to-br from-brand-sand to-orange-600"
                                                                  : "bg-gradient-to-br from-brand-sky to-blue-700"}`}
           data-testid="wallet-balance-hero">
        <div className="flex items-center gap-2 text-white/85 text-xs uppercase tracking-wider font-bold">
          <WalletIcon className="w-3.5 h-3.5"/> Current balance
        </div>
        <div className="font-heading text-5xl font-extrabold mt-2 tracking-tight" data-testid="wallet-balance-amount">
          {formatINR(wallet.balance)}
        </div>
        <div className="text-sm text-white/85 mt-2">
          {low_balance || blocked
            ? "Top up to keep customer OTP & messaging running"
            : <>≈ <b>{Math.max(0, Math.floor(wallet.balance / otp_charge))}</b> OTPs remaining at current rate</>}
        </div>
        {(low_balance || blocked) && (
          <div className="mt-4 rounded-2xl bg-white/15 border border-white/30 px-4 py-3 flex items-start gap-3" data-testid="low-balance-alert">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <div className="text-sm leading-snug">
              {blocked
                ? <><b>OTPs are blocked.</b> Top up immediately — your customers cannot sign in until your balance is above ₹{wallet.block_threshold ?? -50}.</>
                : <><b>Low balance.</b> Top up now to avoid losing customer sign-ups (alert threshold: {formatINR(wallet.low_balance_threshold ?? 50)}).</>}
            </div>
          </div>
        )}
      </div>

      {/* Quick facts */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat label="OTP rate" value={formatINRPrecise(otp_charge)} sub="per SMS / WhatsApp"/>
        <Stat label="Low-balance alert" value={formatINR(wallet.low_balance_threshold ?? 50)} sub="set by platform"/>
        <Stat label="Block below" value={formatINR(wallet.block_threshold ?? -50)} sub="grace overdraft" />
      </div>

      {/* Transactions */}
      <section className="bg-white rounded-3xl border border-neutral-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-neutral-200">
          <h2 className="font-heading font-bold">Recent activity</h2>
          <p className="text-xs text-neutral-500">Last 100 transactions</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider font-bold text-neutral-500">
              <tr>
                <th className="text-left px-5 py-2.5">Date</th>
                <th className="text-left px-5 py-2.5">Type</th>
                <th className="text-left px-5 py-2.5">Description</th>
                <th className="text-right px-5 py-2.5">Amount</th>
                <th className="text-right px-5 py-2.5">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {transactions.map((t) => (
                <tr key={t.id} className="hover:bg-neutral-50" data-testid={`txn-row-${t.id}`}>
                  <td className="px-5 py-2.5 text-xs font-mono text-neutral-500">{t.created_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="px-5 py-2.5">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${
                      t.type === "recharge" || t.type === "bonus" || t.type === "refund" ? "bg-brand-leaf/10 text-brand-leaf"
                      : t.type === "debit_otp" ? "bg-brand-clay/10 text-brand-clay"
                      : "bg-neutral-100 text-neutral-600"
                    }`}>{t.type.replace("_", " ")}</span>
                  </td>
                  <td className="px-5 py-2.5 text-sm">{t.description}</td>
                  <td className={`px-5 py-2.5 text-right font-mono font-bold ${t.amount >= 0 ? "text-brand-leaf" : "text-brand-clay"}`}>
                    {t.amount >= 0 ? <ArrowDownCircle className="inline w-3.5 h-3.5 mr-1"/> : <ArrowUpCircle className="inline w-3.5 h-3.5 mr-1"/>}
                    {t.amount >= 0 ? "+" : ""}{formatINR(t.amount)}
                  </td>
                  <td className="px-5 py-2.5 text-right font-mono text-sm text-neutral-700">{formatINR(t.balance_after)}</td>
                </tr>
              ))}
              {transactions.length === 0 && (
                <tr><td colSpan="5"><EmptyState icon={WalletIcon} size="sm" title="No transactions yet" /></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Recharge dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Recharge platform wallet</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-2xl bg-brand-cream p-4">
              <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Current balance</div>
              <div className="font-heading text-2xl font-extrabold">{formatINR(wallet.balance)}</div>
              <div className="text-xs text-neutral-600 mt-1">≈ {Math.max(0, Math.floor(wallet.balance / otp_charge))} OTPs at {formatINRPrecise(otp_charge)} each</div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Amount</label>
              <div className="grid grid-cols-5 gap-2 mt-2">
                {QUICK.map((v) => (
                  <button key={v} onClick={() => setAmount(v)}
                          className={`h-11 rounded-2xl border-2 font-bold text-sm ${amount === v ? "border-brand-sky bg-brand-sky/10 text-brand-sky" : "border-neutral-200 bg-white text-neutral-700"}`}
                          data-testid={`quick-${v}`}>₹{v}</button>
                ))}
              </div>
              <Input type="number" value={amount} onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
                     className="h-14 rounded-2xl text-lg mt-3" data-testid="recharge-amount-input"/>
            </div>
            <div className="rounded-2xl bg-brand-leaf/10 border border-brand-leaf/30 p-3 flex items-start gap-2 text-sm">
              <Sparkles className="w-4 h-4 text-brand-leaf shrink-0 mt-0.5"/>
              <div>
                You'll get <b>{Math.floor(amount / otp_charge)}</b> additional OTPs.
                Payment via <b>Razorpay</b> (instant) — falls back to demo credit if not configured.
              </div>
            </div>
            <Button onClick={recharge} disabled={busy} className="h-14 w-full rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold text-base" data-testid="confirm-recharge-btn">
              Pay {formatINR(amount)} via Razorpay
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-4">
      <div className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">{label}</div>
      <div className="font-heading font-extrabold text-xl mt-1">{value}</div>
      <div className="text-xs text-neutral-500 mt-0.5">{sub}</div>
    </div>
  );
}
